if (!customElements.get('lookbook-grid')) {
  class LookbookGrid extends HTMLElement {
    constructor() {
      super();

      this.bindOnClick = this.onClick.bind(this);
    }

    connectedCallback() {
      if (this.dataset.type == 'pages') return;

      this.prev = this.querySelector('.lookbook__paginate__trigger[data-direction="prev"]');
      this.next = this.querySelector('.lookbook__paginate__trigger[data-direction="next"]');
      this.section = this.dataset.section;
      this.pageParam = this.dataset.pageParam || 'page';
      this.currentPage = +this.dataset.currentPage;
      this.list = this.querySelector('.lookbook__list');
      this.loadingStatus = this.querySelector('[data-loading-status]');
      this.rendering = false;

      this.initCurrentPages();
      this.initTriggerObserver();
      this.initPageObserver();

      if (this.prev) this.prev.addEventListener('click', this.bindOnClick);
      if (this.next) this.next.addEventListener('click', this.bindOnClick);
    }

    initCurrentPages() {
      this.itemPageElements = Array.from(this.querySelectorAll('.lookbook__item[data-current-page]'));
      this.currentPages = {};
      this.itemPageElements.forEach((element) => {
        const pageKey = element.dataset.currentPage;

        if (!this.currentPages[pageKey]) {
          this.currentPages[pageKey] = { url: '', page: null, first: null, last: null };
        }

        this.currentPages[pageKey].url = element.dataset.currentPageUrl;
        this.currentPages[pageKey].page = +element.dataset.currentPage;

        if (element.hasAttribute('last-from-page')) {
          this.currentPages[pageKey].last = element;
        } else if (element.hasAttribute('first-from-page')) {
          this.currentPages[pageKey].first = element;
        }
      });
    }

    initTriggerObserver() {
      this.triggerObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.rendering) {
              const trigger = entry.target;
              if (trigger.dataset.type === 'auto') {
                this.setLoadingTrigger(trigger, true);
                this.renderItems(trigger);
              }
            }
          });
        },
        { rootMargin: '200px 0px 200px 0px' }
      );

      if (this.prev && this.prev.dataset.type === 'auto') {
        this.triggerObserver.observe(this.prev);
        this.checkTriggerVisibility(this.prev);
      }
      if (this.next && this.next.dataset.type === 'auto') {
        this.triggerObserver.observe(this.next);
        this.checkTriggerVisibility(this.next);
      }
    }

    checkTriggerVisibility(trigger) {
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const isVisible = rect.bottom >= -200 && rect.top <= viewportHeight + 200;
      if (isVisible && !this.rendering) {
        this.setLoadingTrigger(trigger, true);
        this.renderItems(trigger);
      }
    }

    initPageObserver() {
      this.pageObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const page = +entry.target.dataset.currentPage;
              if (page && this.currentPage !== page) {
                this.updatePage(page);
              }
            }
          });
        },
        { rootMargin: '-50% 0px -50% 0px' }
      );

      this.observePageElements();
    }

    observePageElements() {
      if (this.pageObserver) {
        this.itemPageElements.forEach((element) => {
          if (element.hasAttribute('first-from-page')) {
            this.pageObserver.observe(element);
          }
        });
      }
    }

    disconnectedCallback() {
      if (this.prev) this.prev.removeEventListener('click', this.bindOnClick);
      if (this.next) this.next.removeEventListener('click', this.bindOnClick);
      if (this.triggerObserver) this.triggerObserver.disconnect();
      if (this.pageObserver) this.pageObserver.disconnect();
    }

    renderItems(trigger) {
      if (!trigger) return;
      const url = trigger.href || trigger.dataset.target || null;
      const direction = trigger.dataset.direction;
      if (!url) return;

      this.rendering = true;
      this.showError(trigger, false);

      this.setLoadingTrigger(trigger, true);
      const parsedUrl = url.startsWith('/') ? window.location.origin + url : url

      const newUrl = new URL(parsedUrl);
      const params = newUrl.searchParams;
      params.set('section_id', this.section);
      newUrl.search = params.toString();

      fetch(newUrl)
        .then(response => response.text())
        .then(response => {
          const html = new DOMParser().parseFromString(response, 'text/html');
          const currentScroll = document.documentElement.scrollHeight - window.scrollY;
          const currentScrollY = window.scrollY;
          const scrollOffsetY = 300;
          const newScrollY = currentScrollY - scrollOffsetY;

          const newItems = Array.from(html.querySelectorAll('.lookbook__item'));
          const newPrev = html.querySelector('.lookbook__paginate__trigger[data-direction="prev"]');
          const newNext = html.querySelector('.lookbook__paginate__trigger[data-direction="next"]');

          if (direction === 'next') {
            newItems.forEach((item) => {
              this.list.appendChild(item);
            });
            const bottomPage = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight;
            if (bottomPage) {
              window.scrollTo({
                top: newScrollY,
                behavior: 'instant',
              });
            };
            if (newNext) {
              this.next.replaceWith(newNext);
              requestAnimationFrame(() => {
                this.next = this.querySelector('.lookbook__paginate__trigger[data-direction="next"]');
                if (this.next) {
                  this.next.addEventListener('click', this.bindOnClick);
                  if (this.next.dataset.type === 'auto') {
                    this.triggerObserver.observe(this.next);
                  }
                }
              });
            } else if (this.next) {
              this.triggerObserver.unobserve(this.next);
              this.next.remove();
            }
          } else if (direction === 'prev') {
            newItems.forEach((item) => {
              this.list.prepend(item);
            });
            window.scrollTo({
              top: document.documentElement.scrollHeight - currentScroll,
              behavior: 'instant',
            });
            if (newPrev) {
              this.prev.replaceWith(newPrev);
              requestAnimationFrame(() => {
                this.prev = this.querySelector('.lookbook__paginate__trigger[data-direction="prev"]');
                if (this.prev) {
                  this.prev.addEventListener('click', this.bindOnClick);
                  if (this.prev.dataset.type === 'auto') {
                    this.triggerObserver.observe(this.prev);
                  }
                }
              });
            } else if (this.prev) {
              this.triggerObserver.unobserve(this.prev);
              this.prev.remove();
            }
          }
        })
        .catch((err) => {
          console.error(err);
          this.setLoadingTrigger(trigger, false);
          this.showError(trigger, true);
        })
        .finally(() => {
          this.setLoadingTrigger(trigger, false);
          this.initCurrentPages();
          this.observePageElements();
          this.rendering = false;
        })

    }

    onClick(event) {
      event.preventDefault();
      const trigger = event.target.closest('.lookbook__paginate__trigger');

      this.renderItems(trigger);
    }

    setLoadingTrigger(trigger, enableLoading) {
      const spinner = trigger.querySelector('.loading__spinner');
      if (enableLoading) {
        trigger.setAttribute('aria-disabled', true);
        trigger.classList.add('loading');
        if (spinner) spinner.classList.remove('hidden');
        this.announceLoadingStatus(true);
      } else {
        trigger.removeAttribute('aria-disabled');
        trigger.classList.remove('loading');
        if (spinner) spinner.classList.add('hidden');
        this.announceLoadingStatus(false);
      }
    }

    announceLoadingStatus(isLoading) {
      if (!this.loadingStatus) return;
      const loadingText = this.querySelector('[data-loading-text]');
      if (isLoading && loadingText) {
        this.loadingStatus.textContent = loadingText.textContent;
      } else {
        this.loadingStatus.textContent = '';
      }
    }

    showError(trigger, show) {
      if (!trigger) return;
      const direction = trigger.dataset.direction;
      const errorMessage = this.querySelector(`[data-error-message][data-direction="${direction}"]`);
      if (!errorMessage) return;
      if (show) {
        const errorText = window.accessibilityStrings?.loadError || 'Error loading content. Please try again.';
        errorMessage.textContent = errorText;
        errorMessage.classList.remove('hidden');
      } else {
        errorMessage.textContent = '';
        errorMessage.classList.add('hidden');
      }
    }

    updatePage(page) {
      if (this.currentPage == page) return;
      const url = new URL(window.location.href);
      const params = url.searchParams;
      params.set(this.pageParam, `${page}`);
      url.search = params.toString();
      window.history.replaceState({ page: page }, '', url);
      this.currentPage = page;
    }

  }

  customElements.define('lookbook-grid', LookbookGrid);
}